import { GlBadge, GlButton, GlButtonGroup, GlEmptyState, GlIcon, GlLoadingIcon, GlSearchBoxByType, GlSorting, GlTab, GlTabs } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import EMPTY_SVG_URL from '@gitlab/svgs/dist/illustrations/empty-state/empty-catalog-md.svg?url';
import FILTERED_SVG_URL from '@gitlab/svgs/dist/illustrations/empty-state/empty-search-md.svg?url';
import { s__ } from '~/locale';
import { SORT_OPTION_POPULARITY, SORT_OPTION_RELEASED, SORT_OPTION_STAR_COUNT, VERIFICATION_LEVEL_UNVERIFIED } from '~/ci/catalog/constants';
import getCatalogResources from '~/ci/catalog/graphql/queries/get_ci_catalog_resources.query.graphql';
import getCiCatalogBundledResources from '~/ci/catalog/graphql/queries/get_ci_catalog_bundled_resources.query.graphql';
import CatalogHeader from '/builds/gitlab/gitlab-ee/app/assets/javascripts/ci/catalog/components/list/catalog_header.vue?vue3';
import CiResourcesListItem from '/builds/gitlab/gitlab-ee/app/assets/javascripts/ci/catalog/components/list/ci_resources_list_item.vue?vue3';
const SORT_OPTION_NAME = 'NAME';
const SORT_COMPARATORS = {
    [SORT_OPTION_NAME]: (a, b)=>a.name.localeCompare(b.name),
    [SORT_OPTION_RELEASED]: (a, b)=>new Date(a.versions.nodes[0]?.releasedAt) - new Date(b.versions.nodes[0]?.releasedAt),
    [SORT_OPTION_POPULARITY]: (a, b)=>a.last30DayUsageCount - b.last30DayUsageCount,
    [SORT_OPTION_STAR_COUNT]: (a, b)=>a.starCount - b.starCount
};
const DEMO_STATE_POPULATED = 'populated';
const DEMO_STATE_EMPTY_ORG = 'emptyOrg';
const DEMO_STATE_EMPTY_BUNDLED = 'emptyBundled';
const ORG_SORT_ITEMS = [
    {
        value: SORT_OPTION_POPULARITY,
        text: s__('CiCatalog|Usage')
    },
    {
        value: SORT_OPTION_RELEASED,
        text: s__('CiCatalog|Last released')
    },
    {
        value: SORT_OPTION_STAR_COUNT,
        text: s__('CiCatalog|Stars')
    },
    {
        value: SORT_OPTION_NAME,
        text: s__('CiCatalog|Name')
    }
];
const BUNDLED_SORT_ITEMS = [
    {
        value: SORT_OPTION_RELEASED,
        text: s__('CiCatalog|Last released')
    },
    {
        value: SORT_OPTION_NAME,
        text: s__('CiCatalog|Name')
    }
];
// Bundled resources come from `ciCatalogBundledResources`, which returns a leaner
// shape than an org `CiCatalogResource`. Map it into the shape CiResourcesListItem
// expects. Stats/author are hidden for the bundled tab, so those fields are stubbed.
const adaptBundledResource = (node)=>({
        id: node.id,
        name: node.name,
        description: node.description,
        fullPath: node.fullPath,
        webPath: node.fullPath,
        serverFqdn: node.serverFqdn,
        // Bundled resources have no per-record verification level (the "GitLab-maintained"
        // signal is the tab + callout); UNVERIFIED suppresses the per-card badge.
        verificationLevel: VERIFICATION_LEVEL_UNVERIFIED,
        starCount: 0,
        last30DayUsageCount: 0,
        topics: [],
        icon: null,
        archived: false,
        versions: {
            nodes: node.latestReleasedAt ? [
                {
                    name: node.latestVersionName,
                    path: `/${node.fullPath}`,
                    releasedAt: node.latestReleasedAt,
                    author: null
                }
            ] : []
        }
    });
export default {
    name: 'TwoSourceBrowse',
    components: {
        CatalogHeader,
        CiResourcesListItem,
        GlBadge,
        GlButton,
        GlButtonGroup,
        GlEmptyState,
        GlIcon,
        GlLoadingIcon,
        GlSearchBoxByType,
        GlSorting,
        GlTab,
        GlTabs
    },
    data () {
        return {
            orgResources: [],
            bundledResources: [],
            activeTabIndex: 0,
            searchTerm: '',
            orgSort: SORT_OPTION_POPULARITY,
            bundledSort: SORT_OPTION_RELEASED,
            isAscending: false,
            demoState: DEMO_STATE_POPULATED
        };
    },
    apollo: {
        orgResources: {
            query: getCatalogResources,
            variables () {
                return {
                    first: 20
                };
            },
            update (data) {
                return data?.ciCatalogResources?.nodes || [];
            },
            error () {
                this.orgResources = [];
            }
        },
        bundledResources: {
            query: getCiCatalogBundledResources,
            variables () {
                return {
                    first: 20
                };
            },
            update (data) {
                return (data?.ciCatalogBundledResources?.nodes || []).map(adaptBundledResource);
            },
            error () {
                this.bundledResources = [];
            }
        }
    },
    computed: {
        isLoading () {
            return this.isBundledTab ? this.$apollo.queries.bundledResources.loading : this.$apollo.queries.orgResources.loading;
        },
        isBundledTab () {
            return this.activeTabIndex === 1;
        },
        effectiveOrgResources () {
            return this.demoState === DEMO_STATE_EMPTY_ORG ? [] : this.orgResources;
        },
        effectiveBundledResources () {
            return this.demoState === DEMO_STATE_EMPTY_BUNDLED ? [] : this.bundledResources;
        },
        sortItems () {
            return this.isBundledTab ? BUNDLED_SORT_ITEMS : ORG_SORT_ITEMS;
        },
        selectedSort () {
            return this.isBundledTab ? this.bundledSort : this.orgSort;
        },
        selectedSortText () {
            return this.sortItems.find((item)=>item.value === this.selectedSort)?.text;
        },
        sourceResources () {
            return this.isBundledTab ? this.effectiveBundledResources : this.effectiveOrgResources;
        },
        filteredResources () {
            const term = this.searchTerm.trim().toLowerCase();
            const matched = term ? this.sourceResources.filter((resource)=>resource.name.toLowerCase().includes(term)) : [
                ...this.sourceResources
            ];
            const comparator = SORT_COMPARATORS[this.selectedSort];
            if (!comparator) {
                return matched;
            }
            const direction = this.isAscending ? 1 : -1;
            return matched.sort((a, b)=>direction * comparator(a, b));
        },
        isSearching () {
            return this.searchTerm.trim().length > 0;
        },
        showEmptyState () {
            return !this.isLoading && this.filteredResources.length === 0;
        },
        emptyStateLabels () {
            if (this.isSearching) {
                return this.$options.i18n.searchEmpty;
            }
            return this.isBundledTab ? this.$options.i18n.bundledEmpty : this.$options.i18n.orgEmpty;
        }
    },
    methods: {
        onTabChange (index) {
            this.activeTabIndex = index;
            this.searchTerm = '';
        },
        onSortSelect (value) {
            if (this.isBundledTab) {
                this.bundledSort = value;
            } else {
                this.orgSort = value;
            }
        },
        onSortDirectionToggle () {
            this.isAscending = !this.isAscending;
        },
        setDemoState (value) {
            this.demoState = value;
            this.searchTerm = '';
        }
    },
    demoStates: [
        {
            value: DEMO_STATE_POPULATED,
            text: s__('CiCatalog|Populated')
        },
        {
            value: DEMO_STATE_EMPTY_ORG,
            text: s__('CiCatalog|Empty: your organization')
        },
        {
            value: DEMO_STATE_EMPTY_BUNDLED,
            text: s__('CiCatalog|Empty: GitLab-maintained')
        }
    ],
    EMPTY_SVG_URL,
    FILTERED_SVG_URL,
    i18n: {
        orgEmpty: {
            title: s__('CiCatalog|Get started with the CI/CD Catalog'),
            description: s__('CiCatalog|Create a pipeline component repository and make reusing pipeline configurations faster and easier.')
        },
        bundledEmpty: {
            title: s__('CiCatalog|No GitLab-maintained components yet'),
            description: s__('CiCatalog|GitLab-maintained components have not been made available on this cell yet. They will appear here once they are synced.')
        },
        searchEmpty: {
            title: s__('CiCatalog|No components match your search criteria'),
            description: s__('CiCatalog|Edit your search and try again.')
        }
    }
};
